import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { baseUrl: String, days: String };
  static outlets = ["ui--tabs"];

  connect() {
    this.pendingUpdate = false;
    this.setupPopoverObservers();
  }

  disconnect() {
    this.observers?.forEach((observer) => observer.disconnect());
  }

  // Uses MutationObserver because shadcn popovers use data-state attributes with no native close event
  // Only triggers reload when dropdown closes, not on every selection (better UX)
  setupPopoverObservers() {
    const popovers = this.element.querySelectorAll('[data-ui--popover-target="content"]');
    this.observers = Array.from(popovers).map((popover) => {
      const observer = new MutationObserver(() => {
        if (popover.dataset.state === "closed" && this.pendingUpdate) {
          this.reloadMetricsFrame();
          this.pendingUpdate = false;
        }
      });

      observer.observe(popover, { attributes: true, attributeFilter: ["data-state"] });
      return observer;
    });
  }

  onFilterChange() {
    this.pendingUpdate = true;
  }

  changeDays(event) {
    event.preventDefault();
    event.stopPropagation();

    const clickedDays = event.currentTarget.dataset.days || "all";
    this.daysValue = clickedDays;
    this.reloadMetricsFrame(clickedDays);
  }

  reloadMetricsFrame(days = null) {
    const params = this.buildFilterParams(days);

    const frame = document.getElementById("insights_metrics");
    if (frame) {
      this.showLoadingOverlay(frame);
      frame.src = `${this.baseUrlValue}/key_metrics?${params.toString()}`;
      frame.reload();
    }
  }

  buildFilterParams(daysOverride = null) {
    const params = new URLSearchParams();

    const days = daysOverride !== null ? daysOverride : this.daysValue;

    if (days && days !== "undefined" && days !== "null" && days !== "all") {
      params.set("days", days);
      params.set("start_date", this.calculateStartDateWithTimezone(days));
    }

    this.addArrayParams(params, "location_ids");
    this.addArrayParams(params, "agents");

    const chartFilterSelect = document.querySelector('[name="chart_filter"]');
    if (chartFilterSelect?.value) {
      params.set("chart_filter", chartFilterSelect.value);
    }
    return params;
  }

  calculateStartDateWithTimezone(days) {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - parseInt(days));

    const formatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "shortOffset",
    });

    const year = startDate.getFullYear();
    const month = String(startDate.getMonth() + 1).padStart(2, "0");
    const day = String(startDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day} ${formatter.format(startDate)}`;
  }

  addArrayParams(params, paramName) {
    this.element
      .querySelectorAll(`input[type='hidden'][name='${paramName}[]']`)
      .forEach((input) => {
        if (input.value) params.append(`${paramName}[]`, input.value);
      });
  }

  navigateToConversations(event) {
    event.preventDefault();
    this.switchToConversationsTab();
  }

  navigateToConversationsWithTag(event) {
    event.preventDefault();
    this.switchToConversationsTab({ tags: [event.params.tagLabel] });
  }

  switchToConversationsTab(extraParams = {}) {
    const scrollY = window.scrollY;
    const params = this.buildFilterParams();

    if (extraParams.tags) {
      extraParams.tags.forEach((tag) => params.append("tags[]", tag));
    }

    const url = new URL(window.location);
    url.searchParams.set("tab", "conversations");
    window.history.pushState({}, "", url);

    this.activateConversationsTab();

    const conversationsFrame = document.getElementById("conversations");
    if (conversationsFrame) {
      this.showLoadingOverlay(conversationsFrame);
      conversationsFrame.src = `${this.baseUrlValue}/conversations?${params.toString()}`;
      conversationsFrame.reload();

      conversationsFrame.addEventListener(
        "turbo:frame-load",
        () => {
          window.scrollTo(0, scrollY);
        },
        { once: true }
      );
    }
  }

  activateConversationsTab() {
    if (this.hasUiTabsOutlet) {
      this.uiTabsOutlet.index = 1;
    }
  }

  showLoadingOverlay(frame) {
    // Only show spinner if frame takes longer than 1s to load
    const spinnerDelay = 1000;
    let container = null;
    let containerWasRelative = false;

    const removeOverlays = () => {
      frame.querySelectorAll(".loading-overlay").forEach((node) => node.remove());
      if (container && !containerWasRelative) {
        container.classList.remove("relative");
      }
    };

    const cleanup = (event = null) => {
      if (event && event.target.id !== frame.id) return;

      clearTimeout(spinnerTimeout);
      removeOverlays();

      document.removeEventListener("turbo:frame-load", cleanup);
      document.removeEventListener("turbo:frame-render", cleanup);
      document.removeEventListener("turbo:frame-missing", cleanup);
    };

    const showSpinner = () => {
      removeOverlays();
      const overlay = document.createElement("div");
      overlay.className = "loading-overlay";
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      `;
      overlay.innerHTML = `
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      `;

      container = frame.firstElementChild || frame;
      containerWasRelative = container.classList.contains("relative");
      if (!containerWasRelative) {
        container.classList.add("relative");
      }
      container.appendChild(overlay);
    };

    const spinnerTimeout = setTimeout(showSpinner, spinnerDelay);

    // Remove spinner and cancel timeout when frame finishes rendering/loading
    document.addEventListener("turbo:frame-load", cleanup);
    document.addEventListener("turbo:frame-render", cleanup);
    document.addEventListener("turbo:frame-missing", cleanup);
  }
}
